import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { profileService } from '../../services/profileService';
import './ProfileList.css';

const ProfileList = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    profile_type: '',
    department: '',
    is_active: '1',
    search: ''
  });
  
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadProfiles();
  }, [page, filters]);

  const loadProfiles = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Only send non-empty filter values
      const params = { page, limit: 20 };
      if (filters.profile_type) params.profile_type = filters.profile_type;
      if (filters.department) params.department = filters.department;
      if (filters.is_active !== '') params.is_active = filters.is_active;
      if (filters.search) params.search = filters.search;
      
      const response = await profileService.getAllProfiles(params);
      
      if (response.success) {
        setProfiles(response.data.items);
        setTotalPages(response.data.pagination.totalPages);
      }
    } catch (err) {
      setError(err.error?.message || 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleRowClick = (profile) => {
    navigate(`/profiles/${profile.id}`);
  };

  const columns = [
    { key: 'employee_id', header: 'Employee ID', width: '120px' },
    { key: 'full_name', header: 'Name', width: '200px' },
    { key: 'designation', header: 'Designation', width: '180px' },
    { key: 'department', header: 'Department', width: '150px' },
    { 
      key: 'profile_type', 
      header: 'Type', 
      width: '120px',
      render: (value) => (
        <span className={`badge badge-${value}`}>
          {value.charAt(0).toUpperCase() + value.slice(1)}
        </span>
      )
    },
    { 
      key: 'years_of_experience', 
      header: 'Experience', 
      width: '100px',
      render: (value) => `${value} yrs`
    },
    { 
      key: 'is_active', 
      header: 'Status', 
      width: '100px',
      render: (value) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  const canManage = user?.role === 'hr_admin' || user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="profiles-container">
      <div className="profiles-header">
        <h1>Profiles</h1>
        {canManage && (
          <button onClick={() => navigate('/profiles/new')} className="btn-primary">
            + Add Profile
          </button>
        )}
      </div>

      <div className="profiles-filters">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="search-input"
        />
        
        <select
          value={filters.profile_type}
          onChange={(e) => handleFilterChange('profile_type', e.target.value)}
          className="filter-select"
        >
          <option value="">All Types</option>
          <option value="employee">Employee</option>
          <option value="consultant">Consultant</option>
          <option value="external">External</option>
        </select>
        
        <select
          value={filters.is_active}
          onChange={(e) => handleFilterChange('is_active', e.target.value)}
          className="filter-select"
        >
          <option value="1">Active</option>
          <option value="0">Inactive</option>
          <option value="">All Status</option>
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading profiles...</div>
      ) : profiles.length > 0 ? (
        <>
          <div className="profiles-grid">
            {profiles.map(profile => (
              <div key={profile.id} className="profile-card" onClick={() => handleRowClick(profile)}>
                <div className="profile-card-header">
                  <h3>{profile.full_name || `${profile.first_name} ${profile.last_name}`}</h3>
                  <span className={`status-badge ${profile.is_active ? 'active' : 'inactive'}`}>
                    {profile.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="profile-position">{profile.designation || profile.position}</p>
                {profile.department && <p className="profile-department">{profile.department}</p>}
                {profile.location && <p className="profile-location">📍 {profile.location}</p>}
                <div className="profile-card-footer">
                  <span className="profile-type">{profile.profile_type}</span>
                  <span className="profile-skills">⚡ {profile.skills_count || profile.skill_count || 0} skills</span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Page {page} of {totalPages}
              </div>
              <div className="pagination-controls">
                <button
                  className="btn-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  ← Previous
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="no-profiles">
          <p>No profiles found</p>
          {canManage && (
            <button onClick={() => navigate('/profiles/new')} className="btn-primary">
              Create First Profile
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileList;
